<script setup lang="ts">
import Background from '@/shared/components/base/Background/Background.vue';
import { BackgroundType } from '@/shared/components/base/Background/types.ts';
import { IVideo } from '@/shared/components/types.ts';
import { MaybeNull } from '@/shared/types';
import anime, { AnimeTimelineInstance } from 'animejs';
import {
  Ref,
  ref,
} from 'vue';

const props = defineProps<{
  active: boolean,
  video: IVideo;
}>();

const promoVideo$: Ref<MaybeNull<HTMLElement>> = ref(null);
const videoPlayer$: Ref<MaybeNull<typeof Background>> = ref(null);

const animationTimeline: Ref<MaybeNull<AnimeTimelineInstance>> = ref(null);

const closeVideo = () => {
  // if the video started to open, but there was a tap on the screen,
  // then we reverse the opening animation
  return new Promise((resolve) => {
    if (animationTimeline.value) {
      animationTimeline.value.reverse();

      setTimeout(() => {
        resolve(true);
      }, animationTimeline.value.currentTime);

      return;
    }

    animationTimeline.value = anime.timeline({
      complete: () => {
        resolve(true);

        animationTimeline.value = null;
      },
    });

    animationTimeline.value.add({
      targets: promoVideo$.value,
      width: ['100%', '0%'],
      duration: 1500,
      easing: 'easeInOutCubic',
    }, 0);
  });
};

const openVideo = () => {
  return new Promise((resolve, reject) => {
    animationTimeline.value = anime.timeline({
      complete: () => {
        if (animationTimeline.value?.reversed) {
          reject(false);
        } else {
          if (videoPlayer$.value) {
            videoPlayer$.value.container$.firstElementChild.muted = false;
          }
          resolve(true);
        }

        animationTimeline.value = null;
      },
    });

    animationTimeline.value.add({
      targets: promoVideo$.value,
      width: ['0%', '100%'],
      duration: 1500,
      easing: 'easeInOutCubic',
    }, 0);
  });
};

defineExpose({
  closeVideo,
  openVideo,
});

</script>

<template>
  <div
    ref="promoVideo$"
    class="promo-video"
  >
    <Background
      v-if="props.active"
      ref="videoPlayer$"
      :active="true"
      :background="{
        type: BackgroundType.Video,
        video: {
          url: props.video.url,
        },
      }"
    />
  </div>
</template>

<style scoped>
.promo-video {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0%;
  height: 100%;
  z-index: 250;
}
</style>
